import {
  DEFAULT_LOCALE,
  isValidLocaleCode,
  type LocaleCode,
  type SupplierIntegrationEntry,
} from '@choco-admin/types';
import React, { createContext, useContext, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { showsOutOfStockProducts } from '../../utils/buyerVisibleStockStatuses';

import { type SupplierFormData, type TabProps } from './types';
import { supplierFormResolver } from './validation';

interface SupplierFormContextValue {
  mode: TabProps['mode'];
  supplier: TabProps['supplier'];
  formMethods: ReturnType<typeof useForm<SupplierFormData>>;
}

const SupplierFormContext = createContext<SupplierFormContextValue | null>(
  null
);

export const useSupplierForm = () => {
  const context = useContext(SupplierFormContext);
  if (!context) {
    throw new Error(
      'useSupplierForm must be used within a SupplierFormProvider'
    );
  }
  return context;
};

interface SupplierFormProviderProps extends TabProps {
  children: React.ReactNode;
}

const extractMinOrderValue = (
  minOrder?: { price?: { amount?: number | null } | null } | null
): string => {
  if (!minOrder?.price?.amount) return '';
  // Convert cents to whole units (e.g., 1200 -> "12")
  return (minOrder.price.amount / 100).toString();
};

/**
 * When noDefaultCatalog is true the supplier hides all products. The form
 * represents this with the sentinel value 'NO_CATALOG' in the dropdown so the
 * UI shows "Hide all products". On load we must convert the backend flag back
 * to the sentinel; on save we convert it back to { defaultSubCatalog: '',
 * noDefaultCatalog: true }.
 */
const extractDefaultSubCatalog = (
  defaultSubCatalog?: string | null,
  noDefaultCatalog?: boolean | null
): string => {
  if (noDefaultCatalog) return 'NO_CATALOG';
  return defaultSubCatalog ?? '';
};

/**
 * The backend stores locale as a plain string; guard against unknown/legacy
 * values before handing them to the LocaleCode-typed form field.
 */
const extractLocale = (locale?: string | null): LocaleCode =>
  locale && isValidLocaleCode(locale) ? locale : DEFAULT_LOCALE;

/**
 * Extract notification default values from the supplier's existing integration
 * array so the form is pre-seeded when editing.  The form uses comma-separated
 * strings for multi-email fields; that mirrors how buildIntegrationPayload
 * splits them back into arrays.
 */
const extractNotificationDefaults = (
  integration?: SupplierIntegrationEntry[] | null
): Pick<
  SupplierFormData,
  | 'notificationOrderEmails'
  | 'notificationSystemAlertEmails'
  | 'notificationComplaintEmails'
  | 'notificationWhatsappNumber'
  | 'notificationSmsNumber'
  | 'notificationOrderPdf'
  | 'notificationOrderXlsx'
> => {
  const defaults = {
    notificationOrderEmails: '',
    notificationSystemAlertEmails: '',
    notificationComplaintEmails: '',
    notificationWhatsappNumber: '',
    notificationSmsNumber: '',
    notificationOrderPdf: false,
    notificationOrderXlsx: false,
  };

  if (!integration) return defaults;

  return integration.reduce((acc, entry) => {
    if (entry.__typename === 'IntegrationEmailOption') {
      return {
        ...acc,
        // Attachment flags are seeded unconditionally so buildIntegrationPayload
        // round-trips them when the user edits an unrelated field.
        notificationOrderPdf: entry.pdf,
        notificationOrderXlsx: entry.xlsx,
        ...(entry.orderEmails?.length && {
          notificationOrderEmails: entry.orderEmails.join(', '),
        }),
        ...(entry.messageEmails?.length && {
          notificationSystemAlertEmails: entry.messageEmails.join(', '),
        }),
        ...(entry.complaintEmails?.length && {
          notificationComplaintEmails: entry.complaintEmails.join(', '),
        }),
      };
    }
    if (entry.__typename === 'IntegrationWhatsAppOption') {
      return { ...acc, notificationWhatsappNumber: entry.phone };
    }
    if (entry.__typename === 'IntegrationSmsOption') {
      return { ...acc, notificationSmsNumber: entry.phone };
    }
    return acc;
  }, defaults);
};

export const SupplierFormProvider: React.FC<SupplierFormProviderProps> = ({
  mode,
  supplier,
  children,
}) => {
  const notificationDefaults = extractNotificationDefaults(
    supplier?.integration
  );

  // Rebuilt only when the mode changes. `timezone` is required on create and
  // optional on edit, and a resolver rebuilt every render would remount the
  // whole form's validation on each keystroke.
  const resolver = useMemo(() => supplierFormResolver(mode), [mode]);

  const formMethods = useForm<SupplierFormData>({
    resolver,
    mode: 'onChange',
    defaultValues: {
      // Profile defaults
      name: supplier?.name ?? '',
      description: supplier?.description ?? '',
      primaryCategory: supplier?.primaryAssortment ?? '',
      secondaryCategories: supplier?.secondaryAssortments ?? [],
      deliveryFee: supplier?.deliveryCosts ?? '',
      minimumOrderValue: extractMinOrderValue(supplier?.minOrder),
      minimumOrderCurrency: supplier?.minOrder?.price?.currency ?? 'EUR',
      minOrderStrict: supplier?.minOrder?.strict ?? false,

      // Catalog defaults
      supplierDefaultSubCatalog: extractDefaultSubCatalog(
        supplier?.defaultSubCatalog,
        supplier?.noDefaultCatalog
      ),
      displayInMarketplace:
        supplier?.priceSetting?.displayInMarketplace ?? false,
      displayToCustomers: supplier?.priceSetting?.displayToCustomers ?? false,
      supplierHideMyPrice: supplier?.hideMyPrice ?? false,
      showOutOfStockProducts: showsOutOfStockProducts(
        supplier?.buyerVisibleStockStatuses
      ),

      // Order preferences defaults
      allowCustomProducts: supplier?.allowCustomProducts ?? false,
      mandatoryCustomerNumber: supplier?.customerNumberMandatory ?? false,
      mandatoryDeliveryDate: supplier?.deliveryDateMandatory ?? true,
      enableProductComments: supplier?.allowBuyerComment ?? false,
      allowFractionalUnits: supplier?.hasEnabledFractionalUnits ?? false,
      allowOnlyOneVariant: supplier?.allowOnlyOneActiveVariant ?? false,
      sortBy:
        supplier?.sortCartBy === 'alphabetical' ? 'alphabetical' : 'orderGuide',
      cutoffType: 'strict',
      deliveryDays: {},
      cutoffTimes: {},
      cutoffDays: {},

      // Ordering and eCommerce (POR-31). `??` so a stored false survives: these
      // are all opt-in, and `||` would make "off" and "never set" the same value.
      allowInvoiceOrderGuides:
        supplier?.allowInvoiceOrderGuidePhotoUpload ?? false,
      disableCustomerNumberEdits: supplier?.disableCustomerNumberEdits ?? false,
      disableOrderedProductMerging:
        supplier?.allowOrderedProductMerging ?? false,
      enableEcommerceProductGrid:
        supplier?.enableEcommerceProductGridView ?? false,
      allowEcommercePoNumber: supplier?.allowEcommercePoNumber ?? false,
      allowCustomerAcquisition: supplier?.allowCustomerAcquisition ?? false,

      // Settings
      isIntegrated: supplier?.isIntegrated ?? false,
      // Unset means off. The backend treats both as opt-in (PRO-279), so a
      // distributor that has never had them set reads as disabled.
      enablePromotions: supplier?.enablePromotions ?? false,
      enableTaxes: supplier?.enableTaxes ?? false,
      // Unset is meaningful for both: the backend falls back to ORDER_DATE and
      // to the currency's own decimal places. The select shows the effective
      // default; precision stays undefined so nothing is invented.
      pricingDateSource: supplier?.pricingDateSource ?? 'ORDER_DATE',
      calculationPrecision: supplier?.calculationPrecision ?? undefined,
      referenceId: supplier?.referenceId ?? '',
      locale: extractLocale(supplier?.locale),
      timezone: supplier?.timezone ?? '',

      // Notification addresses — seeded from the supplier's existing integration
      // data so editing any field doesn't wipe the rest.
      ...notificationDefaults,
    },
  });

  return (
    <SupplierFormContext.Provider value={{ mode, supplier, formMethods }}>
      <FormProvider {...formMethods}>{children}</FormProvider>
    </SupplierFormContext.Provider>
  );
};
